<script setup lang="ts">
import { app } from '@/composition/app'
import { GroupState } from '@/composition/groups'
import { Theme } from '@/composition/themes'
import { UserState } from '@/composition/users'
import { replaceBucketUrlWithCdnUrl } from '@/helper/ImageUpload.helper'
import { DEFAULT_GROUP_LOGO } from '@/helper/const'
import { useGlobalStore } from '@/store/app'
import { handleScroll } from '@/util/scroll'
import { UIAvatar } from '@gohighlevel/ghl-ui'
import { defineAsyncComponent, h, onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { truncateString } from '../../../helper'
import CommunitiesTabSwitcher from './CommunitiesTabSwitcher.vue'

const ChatsWeb = defineAsyncComponent(() => import('./ChatsWeb.vue'))

const route = useRoute()

const options = ref([])
const selectedGroup = ref()
const selectedGroupObj = ref()
const isLoading = ref(true)
const chatConversationListIsOpen = ref(false)
const shouldShowChat = ref(false)
const appStore = useGlobalStore()
const isVisible = ref(true)

function toggleChatConversationsList() {
  chatConversationListIsOpen.value = !chatConversationListIsOpen.value
}

onMounted(async () => {
  // Add scroll event listener only for mobile screens
  const scrollHandler = handleScroll(visible => {
    if (app.state.isMobile) {
      isVisible.value = visible
    }
  })
  window.addEventListener('scroll', scrollHandler)

  // Queue chat mounting for when page is completely loaded
  if (document.readyState === 'complete') {
    // Page is already loaded
    shouldShowChat.value = true
  } else {
    // Wait for page to complete loading
    document.onreadystatechange = () => {
      if (document.readyState === 'complete') {
        shouldShowChat.value = true
      }
    }
  }

  //Filter Group which has groupSwitcherVisibility as false
  const groupSwitcher = GroupState.state.groupList
  options.value = groupSwitcher.map((group: any) => {
    const groupOption = {
      id: group.name,
      key: group.slug,
      label: truncateString(group.name, 30),
      imageUrl:
        replaceBucketUrlWithCdnUrl(group?.branding?.logo) || DEFAULT_GROUP_LOGO,
      icon: () => {
        const groupAvatar = h(UIAvatar, {
          style: {
            margin: '8px',
          },
          size: 24,
          class:
            'font-medium text-communities-font-secondary bg-communities-background text-md',
          src: group?.branding?.logo || DEFAULT_GROUP_LOGO,
        })
        return groupAvatar
      },
    }
    if (route.params.groupSlug === group.slug) {
      groupOption['props'] = {
        class:
          'bg-communities-background text-sm text-communities-font-primary rounded-xl',
      }
      selectedGroupObj.value = groupOption
      selectedGroup.value = groupOption.key
    } else {
      groupOption['props'] = {
        class: 'text-sm text-communities-font-primary',
      }
    }
    return groupOption
  })
  isLoading.value = false

  // Cleanup scroll listener on unmount
  onUnmounted(() => {
    window.removeEventListener('scroll', scrollHandler)
  })
})
</script>
<template>
  <div
    class="lg:mobile-padding communities-top-nav z-30 w-full items-center lg:px-0"
    :class="[
      Theme.state.isSideBarBackgroundDark && Theme.state.windowGradient
        ? 'bg-communities-sidebar-background-gradient'
        : 'bg-communities-sidebar-background',
      false &&
        'border border-l-0 border-r-0 border-t-0 border-solid border-communities-border',
      app.state.isMobile ? 'mobile-nav' : 'desktop-nav',
      !isVisible && 'nav-hidden',
      app.state.isMobile && !appStore.isAuthenticated && 'mobile-nav-with-banner',
    ]"
  >
    <div
      class="overflow-x-auto bg-communities-background"
      :class="
        !app.state.readingMode &&
        Theme?.state?.windowGradient &&
        'rounded-tl-xs'
      "
    >
      <div class="flex w-full min-w-max items-center justify-around">
        <CommunitiesTabSwitcher />
        <ChatsWeb
          v-if="shouldShowChat && UserState.state?.currentUser?.fullName"
          id="communities-top-nav-bar-chats-web"
          class="absolute right-5"
          :chat-conversation-list-is-open="chatConversationListIsOpen"
          @update:chat-conversation-list-is-open="toggleChatConversationsList"
        />
      </div>
    </div>
  </div>
</template>
<style scoped>
.communities-top-nav {
  transition: transform 0.3s ease;
}

.desktop-nav {
  position: sticky;
  top: 58px;
}

.mobile-nav {
  position: fixed;
  left: 0;
  right: 0;
  top: 48px;
}

.mobile-nav-with-banner {
  position: fixed;
  left: 0;
  right: 0;
  top: 96px; /* 48px banner + 48px TopNavBarMobile */
}

@media (max-width: 930px) {
  .nav-hidden {
    transform: translateY(-100%);
  }
}

/* Hide scrollbar for Chrome, Safari and Opera */
.overflow-x-auto::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.overflow-x-auto {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}
</style>
